import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { Plus, Pencil, Trash2, X, MessageSquare, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Toaster } from '../../components/ui/sonner';

const AdminTestimonials = () => {
  const { token } = useAuth();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '', role: '', content: '', rating: 5, project: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/testimonials`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setTestimonials(await response.json());
    } catch (error) {
      toast.error('Erro ao carregar depoimentos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingItem 
        ? `${API_URL}/api/admin/testimonials/${editingItem.id}`
        : `${API_URL}/api/admin/testimonials`;
      
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingItem ? 'Depoimento atualizado!' : 'Depoimento criado!');
        setShowModal(false);
        resetForm();
        fetchData();
      }
    } catch (error) {
      toast.error('Erro ao salvar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja eliminar este depoimento?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/testimonials/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Depoimento eliminado!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao eliminar');
    }
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name, role: item.role, content: item.content,
      rating: item.rating, project: item.project
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({ name: '', role: '', content: '', rating: 5, project: '' });
  };

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />
      
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Depoimentos</h1>
            <p className="text-gray-500">Gerencie as avaliações de clientes</p>
          </div>
          <Button onClick={() => { setShowModal(true); resetForm(); }} className="bg-orange-500 hover:bg-orange-600">
            <Plus size={18} className="mr-2" />
            Novo Depoimento
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {testimonials.map((item) => (
              <Card key={item.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                        <span className="text-orange-600 font-bold">{item.name.charAt(0)}</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{item.name}</h3>
                        <p className="text-sm text-gray-500">{item.role}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Button variant="ghost" size="sm" onClick={() => openEditModal(item)}>
                        <Pencil size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(item.id)}>
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center mb-2">
                    {[...Array(item.rating)].map((_, i) => (
                      <Star key={i} size={16} className="text-yellow-500 fill-yellow-500" />
                    ))}
                  </div>
                  <p className="text-gray-600 text-sm line-clamp-3">"{item.content}"</p>
                  {item.project && <Badge variant="outline" className="mt-2">{item.project}</Badge>}
                </CardContent>
              </Card>
            ))}
            {testimonials.length === 0 && (
              <div className="col-span-2 text-center py-12 text-gray-500">Nenhum depoimento encontrado</div>
            )}
          </div>
        )}

        <p className="text-sm text-gray-500">Total: {testimonials.length} depoimentos</p>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{editingItem ? 'Editar Depoimento' : 'Novo Depoimento'}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}><X size={18} /></Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Nome do Cliente *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Ex: João Silva" required />
                </div>
                <div>
                  <Label>Cargo/Função *</Label>
                  <Input value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} placeholder="Ex: Síndico - Condomínio Solar" required />
                </div>
                <div>
                  <Label>Avaliação</Label>
                  <div className="flex items-center space-x-2 mt-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setFormData({ ...formData, rating: star })}
                        className="focus:outline-none"
                      >
                        <Star
                          size={24}
                          className={star <= formData.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Depoimento *</Label>
                  <Textarea value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} rows={4} required placeholder="O que o cliente disse sobre o serviço..." />
                </div>
                <div>
                  <Label>Projeto Relacionado</Label>
                  <Input value={formData.project} onChange={(e) => setFormData({ ...formData, project: e.target.value })} placeholder="Ex: Reforma de Fachada" />
                </div>
                <div className="flex space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">Cancelar</Button>
                  <Button type="submit" className="flex-1 bg-orange-500 hover:bg-orange-600">{editingItem ? 'Atualizar' : 'Criar'}</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminTestimonials;
